/**
 * <uterific-statement> — the scroll-linked word reveal on a statement block.
 *
 * Writes one progress value, --ut-p, on the host: 0 when the block's top is `start` of
 * the way down the viewport, 1 when its bottom has reached `end` of the way down. The section's CSS gives every word its own index and fades it in as the value
 * passes it, so this file never touches the words themselves.
 *
 * Attributes:
 *   start   viewport fraction the block's top has reached when the reveal begins
 *           (default 0.65: it has come a third of the way up the screen, not just appeared)
 *   end     viewport fraction the block's bottom reaches when it is complete (default 0.5)
 *
 * Reduced motion: progress pins at 1 and scroll is not tracked. Without this script the
 * CSS default is also 1, so the sentence is always readable.
 *
 * Above 990px Horizon scrolls inside .page-wrapper rather than the window, and scroll does
 * not bubble, so the listener captures on the document — see uterific-scroll.js.
 *
 * Load this with type="module".
 */

const clamp = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

class UterificStatement extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.style.setProperty('--ut-p', '1');
      return;
    }

    this.start = parseFloat(this.getAttribute('start')) || 0.65;
    this.end = parseFloat(this.getAttribute('end')) || 0.5;

    this.frame = 0;
    this.onScroll = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.measure());
    };

    this.measure();
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.onScroll);
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  measure() {
    this.frame = 0;
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;
    const from = vh * this.start;
    const to = vh * this.end - rect.height;
    // A block taller than the window's reveal band would never finish: show it whole.
    const p = from <= to ? 1 : clamp((from - rect.top) / (from - to));
    this.style.setProperty('--ut-p', p.toFixed(4));
  }
}

if (!customElements.get('uterific-statement')) {
  customElements.define('uterific-statement', UterificStatement);
}
